import { NavLink } from 'react-router-dom';
import { 
  Home,
  Users,
  Shield,
  FileDown,
  Network,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Lock,
  Inbox,
  FileText,
  Store
} from 'lucide-react';
import { useState } from 'react';
import { useAuthState } from '@/auth/AuthProvider';
import type { Professional } from '@/types/professional';
import type { ProStatusResolution } from '@/domain/pro/resolveProStatus';

interface ProPortalSidebarProps {
  professional?: Professional | null;
  resolution?: ProStatusResolution;
}

interface NavItem {
  name: string;
  icon: typeof Home;
  path: string;
  end?: boolean;
}

function SidebarLink({ 
  item, 
  isCollapsed, 
  isGated 
}: { 
  item: NavItem; 
  isCollapsed: boolean; 
  isGated: boolean;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.end}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition relative ${
          isActive
            ? 'bg-slate-800 text-white'
            : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
        } ${isCollapsed ? 'justify-center' : ''} ${isGated ? 'opacity-60' : ''}`
      }
      title={isCollapsed ? item.name : undefined}
    >
      <Icon size={20} className="flex-shrink-0" />
      {!isCollapsed && (
        <>
          <span>{item.name}</span>
          {isGated && <Lock size={14} className="ml-auto text-orange-400" />}
        </>
      )}
    </NavLink>
  );
}

export default function ProPortalSidebar({ professional, resolution }: ProPortalSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user } = useAuthState();

  // Use resolution from props (computed by centralized resolver)
  const badges = resolution?.badges || {
    verified: false,
    listed: false,
    hasSubscription: false,
    showLicenseWarning: false,
    daysUntilExpiration: null,
  };
  
  const gatedRoutes = resolution?.gatedRoutes || {
    reviewRequests: true,
    conflictChecks: true,
    exports: true,
    marketplaceListing: true,
  };

  const navItems: NavItem[] = [
    {
      name: 'Dashboard',
      icon: Home,
      path: '/pro/dashboard',
      end: true
    },
    { 
      name: 'Clients', 
      icon: Users, 
      path: '/pro/dashboard/clients'
    },
    { 
      name: 'My Listing', 
      icon: Store, 
      path: '/pro/dashboard/listing'
    },
    { 
      name: 'Requests', 
      icon: Inbox, 
      path: '/pro/requests'
    },
    { 
      name: 'Forms', 
      icon: FileText, 
      path: '/pro/dashboard/forms'
    },
    { 
      name: 'Conflict Checks', 
      icon: Shield, 
      path: '/pro/conflicts'
    },
    { 
      name: 'Integrations', 
      icon: Network, 
      path: '/pro/dashboard/integrations'
    },
    { 
      name: 'Exports', 
      icon: FileDown, 
      path: '/pro/dashboard/exports'
    },
    { 
      name: 'Billing', 
      icon: DollarSign, 
      path: '/pro/billing'
    },
  ];

  const sidebarWidth = isCollapsed ? 'w-[68px]' : 'w-56';

  return (
    <aside className={`${sidebarWidth} relative flex flex-col border-r border-slate-700/50 bg-slate-900/50 backdrop-blur-sm text-slate-100 transition-all duration-200`}>
      {/* Collapse/Expand Toggle */}
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-6 z-10 rounded-full border border-slate-700 bg-slate-800 p-1 text-slate-400 shadow-md transition hover:bg-slate-700"
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {/* Navigation Content */}
      <nav className="flex-1 overflow-y-auto p-3">
        {!isCollapsed && (
          <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Navigation
          </div>
        )}
        <div className="space-y-1">
          {navItems.map((item) => {
            // Determine if route is gated based on path
            let isGated = false;
            if (item.path === '/pro/requests') {
              isGated = gatedRoutes.reviewRequests;
            } else if (item.path === '/pro/conflicts') {
              isGated = gatedRoutes.conflictChecks;
            } else if (item.path === '/pro/dashboard/exports') {
              isGated = gatedRoutes.exports;
            } else if (item.path === '/pro/dashboard/listing') {
              isGated = gatedRoutes.marketplaceListing;
            }
            
            return (
              <SidebarLink
                key={item.path}
                item={item}
                isCollapsed={isCollapsed}
                isGated={isGated}
              />
            );
          })}
        </div>
      </nav>

      {/* Footer: signed-in identity */}
      {!isCollapsed && user?.email && (
        <div className="border-t border-slate-700/50 px-4 py-3">
          <p className="text-xs font-medium text-slate-500">Signed in as</p>
          <p className="mt-0.5 truncate text-sm text-slate-400">{user.email}</p>
        </div>
      )}
    </aside>
  );
}
